// Dashboard language: German (default) or English, switched from the header
// and remembered per browser. Every user-facing string in the SPA goes
// through `t()`; the two dictionaries are typed against each other so a key
// missing in one language fails `tsc`, not the reader.
//
// Deliberately NOT localized: number and date formats (Swiss convention is a
// brand rule — 1'000, DD.MM.YYYY — independent of the reading language),
// product names (Multicheck®, WayFi®, …), chat-mode ids, model ids, and any
// value the backend reports verbatim (window labels, tenant ids, URLs).

import { signal } from "@preact/signals";

import { de } from "./de";
import { en } from "./en";

export type Lang = "de" | "en";
export const LANGS: readonly Lang[] = ["de", "en"];
export const DEFAULT_LANG: Lang = "de";

export type TranslationKey = keyof typeof de;

const STORAGE_KEY = "gw_admin_lang";

const isLang = (value: unknown): value is Lang => value === "de" || value === "en";

function readStoredLang(): Lang {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isLang(stored) ? stored : DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

// The document tag is the full locale, not the toggle value: de-CH is what
// carries the Swiss conventions the browser applies (hyphenation, spell-check),
// and it is what index.html ships statically.
const DOCUMENT_LANG: Record<Lang, string> = { de: "de-CH", en: "en" };

function applyDocumentLang(next: Lang): void {
  if (typeof document !== "undefined") {
    document.documentElement.lang = DOCUMENT_LANG[next];
  }
}

/** The active dashboard language. Read inside render so a switch re-renders. */
export const lang = signal<Lang>(readStoredLang());
applyDocumentLang(lang.value);

export function setLang(next: Lang): void {
  lang.value = next;
  applyDocumentLang(next);
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Private mode / blocked storage — the choice lasts for the tab only.
  }
}

const DICTS: Record<Lang, Record<string, string>> = { de, en };

type Params = Record<string, string | number>;

function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

/** Translate a known key in the active language. `{name}` placeholders are
 * filled from `params`. Falls back to German, then to the key itself, so a
 * gap is visible on screen instead of throwing. */
export function t(key: TranslationKey, params?: Params): string {
  const raw = DICTS[lang.value][key] ?? de[key] ?? key;
  return interpolate(raw, params);
}

/** Translate a key that is built at runtime from backend data (a metric key,
 * an anomaly kind, a dependency name). Returns `fallback` — usually the
 * backend's own text — when no translation exists, so a new backend id never
 * renders as a raw key. */
export function tOr(key: string, fallback: string, params?: Params): string {
  const raw = DICTS[lang.value][key] ?? de[key as TranslationKey];
  return interpolate(raw ?? fallback, params);
}

/** Whether a runtime key has a translation in the active language. */
export function has(key: string): boolean {
  return key in DICTS[lang.value] || key in de;
}
